import React, { useState, useEffect } from 'react';
import { setup2FA, verify2FASetup } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ShieldCheck, Loader2, Smartphone } from 'lucide-react';
import SunsetBackground from '../components/ui/SunsetBackground';

const TwoFASetupPage = () => {
  const { updateToken } = useAuth();
  const [qrCode, setQrCode] = useState(null);
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const data = await setup2FA();
        setQrCode(data.qr_code);
        setSecret(data.secret);
      } catch (error) {
        const msg = error.response?.data?.detail || 'Failed to set up 2FA';
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) {
      toast.error('Enter a 6-digit code');
      return;
    }
    setVerifying(true);
    try {
      const data = await verify2FASetup(code);
      if (data.access_token) {
        updateToken(data.access_token);
      }
      toast.success('2FA enabled successfully!');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Invalid code';
      toast.error(msg);
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      <SunsetBackground />
      <Card className="w-full max-w-md bg-white/95 backdrop-blur-xl border-white/40 shadow-2xl shadow-amber-900/20 relative z-10" data-testid="2fa-setup-card">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-14 h-14 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-3">
            <ShieldCheck className="w-7 h-7 text-[#D4AF37]" />
          </div>
          <CardTitle className="text-xl text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            Set Up Two-Factor Authentication
          </CardTitle>
          <p className="text-sm text-gray-500 mt-2">
            2FA is required to access the platform. Scan the QR code below with your Google Authenticator app.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
            </div>
          ) : (
            <>
              {/* QR Code */}
              {qrCode && (
                <div className="flex flex-col items-center gap-4">
                  <div className="p-3 bg-white rounded-xl border-2 border-[#D4AF37]/20 shadow-sm">
                    <img src={qrCode} alt="2FA QR Code" className="w-48 h-48" data-testid="2fa-qr-code" />
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-400 mb-1">Can't scan? Enter this key manually:</p>
                    <code className="text-xs bg-gray-100 px-3 py-1.5 rounded-md text-gray-700 select-all" data-testid="2fa-secret">
                      {secret}
                    </code>
                  </div>
                </div>
              )}

              {/* Steps */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                <div className="flex items-start gap-3 mb-3">
                  <Smartphone className="w-5 h-5 text-[#D4AF37] mt-0.5 shrink-0" />
                  <div className="text-sm text-gray-600">
                    <p className="font-medium text-gray-800 mb-1">Steps:</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Open Google Authenticator on your phone</li>
                      <li>Tap + and scan the QR code above</li>
                      <li>Enter the 6-digit code shown in the app</li>
                    </ol>
                  </div>
                </div>
              </div>

              {/* Verify */}
              <form onSubmit={handleVerify} className="space-y-4">
                <div>
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit code"
                    className="text-center text-2xl tracking-[0.5em] bg-gray-50 border-gray-200 h-14"
                    autoFocus
                    data-testid="2fa-code-input"
                  />
                </div>
                <Button type="submit" disabled={verifying || code.length !== 6}
                  className="w-full btn-gold h-12 text-base" data-testid="2fa-verify-btn">
                  {verifying ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Verifying...</> : 'Activate 2FA'}
                </Button>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TwoFASetupPage;
